import React, { useState, useRef, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import { Icons } from '@/theme/Images'
import PropTypes from 'prop-types'

const SearchInput = React.forwardRef(
  (
    {
      placeholder = 'Search...',
      options = [],
      value,
      onChange,
      onSelect,
      onBlur,
      className,
      disabled = false,
      noResultsText = 'No options found',
      hasError = false,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchValue, setSearchValue] = useState(value || '')
    const [filteredOptions, setFilteredOptions] = useState(options)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const inputRef = useRef(null)

    // Filter options based on search value
    useEffect(() => {
      if (searchValue.trim() === '') {
        setFilteredOptions(options)
      } else {
        const filtered = options.filter(option => option.toLowerCase().includes(searchValue.toLowerCase()))
        setFilteredOptions(filtered)
      }
      setHighlightedIndex(-1) // Reset highlight when options change
    }, [searchValue, options])

    const handleOpenChange = open => {
      setIsOpen(open)
      if (!open) {
        setHighlightedIndex(-1)
        // Delay onBlur slightly so parent onSelect/onChange state updates settle
        setTimeout(() => {
          onBlur?.()
        }, 0)
      }
    }

    const handleInputChange = e => {
      const newValue = e.target.value
      setSearchValue(newValue)
      setIsOpen(true)
      onChange?.(newValue)
    }

    const handleOptionSelect = option => {
      setSearchValue(option)
      setIsOpen(false)
      onSelect?.(option)
    }

    const handleClear = () => {
      setSearchValue('')
      setIsOpen(false)
      onChange?.('')
      inputRef.current?.focus()
    }

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        setHighlightedIndex(-1)
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        } else if (filteredOptions.length > 0) {
          setHighlightedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0))
        } else {
          // When no options, highlight the create button (index 0)
          setHighlightedIndex(0)
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (isOpen) {
          if (filteredOptions.length > 0) {
            setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1))
          } else {
            // When no options, highlight the create button (index 0)
            setHighlightedIndex(0)
          }
        }
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (isOpen) {
          if (filteredOptions.length > 0 && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleOptionSelect(filteredOptions[highlightedIndex])
          } else if (filteredOptions.length === 0) {
            // When no options, create new
            handleCreateNew()
          }
        }
      }
    }

    const handleInputClick = () => {
      if (!isOpen) {
        setIsOpen(true)
      }
    }

    const handleCreateNew = () => {
      setIsOpen(false)
      setHighlightedIndex(-1)
      // Keep the current search value in the input
    }

    return (
      <div className="relative">
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <div className="relative">
              <Input
                ref={ref || inputRef}
                type="text"
                placeholder={placeholder}
                value={searchValue}
                onChange={handleInputChange}
                onBlur={onBlur}
                onKeyDown={handleKeyDown}
                onClick={handleInputClick}
                disabled={disabled}
                className={cn('pr-10 text-base placeholder:text-[#667085] placeholder:font-light', className)}
                {...props}
              />
              {hasError && !isOpen && (
                <div
                  className={`absolute top-1/2 transform -translate-y-1/2 pointer-events-none ${searchValue ? 'right-10' : 'right-3'}`}
                >
                  <Icons.FailureAlertIconSimple />
                </div>
              )}
              {searchValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0 bg-white"
            align="start"
            onOpenAutoFocus={e => e.preventDefault()}
          >
            <div className="max-h-60 overflow-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <button
                    key={index}
                    type="button"
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm focus:outline-none',
                      index === highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-100'
                    )}
                    onMouseDown={e => {
                      // Prevent input blur before selection handler runs
                      e.preventDefault()
                    }}
                    onClick={() => handleOptionSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option}
                  </button>
                ))
              ) : (
                <div className="p-3">
                  <div className="text-sm text-gray-500 mb-3">{noResultsText}</div>
                  <button
                    type="button"
                    className={cn(
                      'w-full px-4 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                      highlightedIndex === 0
                        ? 'bg-gray-50 border-gray-400'
                        : 'bg-white border-gray-300 hover:bg-gray-50'
                    )}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid var(--Gray-300, #D0D5DD)',
                      background: highlightedIndex === 0 ? 'var(--Gray-50, #F9FAFB)' : 'var(--Base-White, #FFF)',
                      boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.05)',
                      color: 'var(--Gray-700, #344054)',
                      fontSize: '14px',
                      height: '36px',
                    }}
                    onClick={handleCreateNew}
                    onMouseEnter={() => setHighlightedIndex(0)}
                  >
                    <span className="text-2xl -mt-1 font-light">+</span>
                    Create {searchValue || 'new group'}
                  </button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)

SearchInput.displayName = 'SearchInput'

SearchInput.propTypes = {
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  noResultsText: PropTypes.string,
  hasError: PropTypes.bool,
}

export { SearchInput }
